import React, { useEffect, useRef } from 'react';

const cx = (...c) => c.filter(Boolean).join(' ');

/**
 * Progressive-enhancement fade/rise on scroll. SSR + no-JS render fully visible.
 * IMPORTANT: elements already within the viewport on mount are NOT armed — this
 * avoids the visible→hidden→fade flash (a layout shift) on page load. Only
 * below-the-fold elements get the reveal animation.
 */
const Reveal = ({
  children, as: Tag = 'div', className, delay = 0, ...rest
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    // Already visible on load? Leave it as-is — no flash.
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh * 0.92) return undefined;

    el.classList.add('is-armed');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          el.style.transitionDelay = `${delay}ms`;
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);

  return <Tag ref={ref} className={cx('ss-reveal', className)} {...rest}>{children}</Tag>;
};

export default Reveal;
